import { Box, Button, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import {
  type ReactElement,
  type ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import { useNylasGrantInfo } from '../../../hooks/useNylasGrantInfo';

export type NylasContextRef = {
  isAuthorized: boolean | undefined;
  showErrorDialog: () => void;
};

type Props = {
  children: ReactNode;
  // If true, the error dialog will not be shown by default. Use ref to show it.
  isErrorDialogHiddenByDefault?: boolean;
};

export const NylasContext = forwardRef<NylasContextRef, Props>(
  ({ children, isErrorDialogHiddenByDefault = false }, ref): ReactElement => {
    const [authorized, setAuthorized] = useState<boolean | undefined>();
    const [open, setOpen] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      isAuthorized: authorized,
      showErrorDialog: (): void => {
        setOpen(true);
      },
    }));

    const { error, isError, isSuccess } = useNylasGrantInfo();
    useEffect(() => {
      if (isSuccess) {
        setAuthorized(true);
      }
    }, [isSuccess]);
    useEffect(() => {
      if (isError) {
        const badAuth = !!(error.response?.status === 401);
        if (badAuth) {
          setAuthorized(false);
          if (!isErrorDialogHiddenByDefault) {
            setOpen(true);
          }
        }
      }
    }, [isError, error, isErrorDialogHiddenByDefault]);

    return (
      <>
        <Dialog
          open={authorized !== true && open}
          onClose={(): void => {
            setOpen(false);
          }}
        >
          <DialogTitle>Not Authorized</DialogTitle>
          <DialogContent>
            <Box display='flex' flexDirection='column'>
              <Box>
                <Typography>
                  You are not currently authorized to use the emailing feature. Please click on the
                  &quot;Email Options&quot; menu item or follow{' '}
                  <Link to='/emailconfig'>this link</Link> to authorize your email account.
                </Typography>
              </Box>
              <Box flexGrow={1} alignSelf='end'>
                <Button
                  onClick={(): void => {
                    setOpen(false);
                  }}
                >
                  Ok
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
        {children}
      </>
    );
  },
);
